import { useState } from 'react';
import type { AddIpEntryData } from '../../api/ip-allowlist';

interface CidrFormProps {
  onSubmit: (data: AddIpEntryData) => Promise<void>;
  onCancel: () => void;
}

export function CidrForm({ onSubmit, onCancel }: CidrFormProps) {
  const [cidr, setCidr] = useState('');
  const [scope, setScope] = useState('global');
  const [label, setLabel] = useState('');
  const [enforce, setEnforce] = useState<'waf' | 'application' | 'both'>('both');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    // Basic CIDR validation (IPv4 and IPv6)
    const ipv4Cidr = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\/\d{1,2}$/;
    const ipv6Cidr = /^[0-9a-fA-F:]+\/\d{1,3}$/;
    if (!ipv4Cidr.test(cidr.trim()) && !ipv6Cidr.test(cidr.trim())) {
      setError('Invalid CIDR format. Examples: 10.0.0.0/8 or 2600:1700::/32');
      setSubmitting(false);
      return;
    }

    try {
      await onSubmit({
        cidr: cidr.trim(),
        scope,
        label: label.trim(),
        enforce,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add CIDR');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="alert alert--error" style={{ marginBottom: 16 }}>
          <span>{error}</span>
        </div>
      )}

      <div className="modal__field">
        <label htmlFor="cidr-value">CIDR Block</label>
        <input
          id="cidr-value"
          type="text"
          value={cidr}
          onChange={(e) => setCidr(e.target.value)}
          placeholder="10.0.0.0/8"
          required
          autoFocus
        />
      </div>

      <div className="modal__field">
        <label htmlFor="cidr-scope">Scope</label>
        <select
          id="cidr-scope"
          className="modal__select"
          value={scope}
          onChange={(e) => setScope(e.target.value)}
        >
          <option value="global">Global</option>
          <option value="project">Project</option>
          <option value="user">User</option>
        </select>
      </div>

      <div className="modal__field">
        <label htmlFor="cidr-label">Label</label>
        <input
          id="cidr-label"
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Office VPN"
        />
      </div>

      <div className="modal__field">
        <label htmlFor="cidr-enforce">Enforce</label>
        <select
          id="cidr-enforce"
          className="modal__select"
          value={enforce}
          onChange={(e) => setEnforce(e.target.value as 'waf' | 'application' | 'both')}
        >
          <option value="waf">WAF Only</option>
          <option value="application">Application Only</option>
          <option value="both">Both</option>
        </select>
      </div>

      <div className="modal__actions">
        <button type="button" className="modal__cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="modal__submit" disabled={submitting}>
          {submitting ? 'Adding...' : 'Add CIDR'}
        </button>
      </div>
    </form>
  );
}
